import { motion, AnimatePresence } from 'framer-motion';
import Link from 'next/link';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { usePathname } from 'next/navigation';

const MobileNavItem = ({ data, expandedSections, toggleSection, closeMenu }) => {
	const pathName = usePathname();

	// Redirect mappings: original href -> actual destination
	const redirectMap = {
		'/services/ai-services': '/services/ai-as-a-service',
		'/ai-consultant-services': '/services/ai-consulting-services',
	};

	// Helper function to normalize paths (remove trailing slashes, query params)
	const normalizePath = (path) => {
		if (!path) return '';
		// Remove trailing slash (except for root)
		let normalized = path === '/' ? path : path.replace(/\/$/, '');
		// Remove query parameters and hash
		normalized = normalized.split('?')[0].split('#')[0];
		return normalized;
	};

	// Helper function to check if a route is active
	const isActiveRoute = (href) => {
		if (!href) return false;
		
		const normalizedHref = normalizePath(href);
		const normalizedPathName = normalizePath(pathName);
		
		// Handle exact matches
		if (normalizedPathName === normalizedHref) return true;
		
		// Check redirect destination
		const redirectDestination = redirectMap[normalizedHref];
		if (redirectDestination && normalizedPathName === normalizePath(redirectDestination)) {
			return true;
		}
		
		// Handle parent routes (e.g., /services matches /services/ai-services)
		if (normalizedHref !== '/' && normalizedPathName.startsWith(normalizedHref + '/')) {
			return true;
		}
		
		return false;
	};

	// Simple link without dropdown
	if (data.type === 'link' && !data.isDropdown) {
		const isActive = isActiveRoute(data.href);
		return (
			<motion.div
				variants={{
					hidden: {
						x: 20,
						opacity: 0,
					},
					visible: {
						x: 0,
						opacity: 1,
						transition: {
							duration: 0.3,
						},
					},
				}}>
				{isActive ? (
					<span className='flex items-center gap-3 py-3 px-4 text-[#058BF0] bg-blue-50 text-base font-bold cursor-default rounded-lg transition-all duration-200'>
						<span>{data.label}</span>
					</span>
				) : (
					<Link
						className='flex items-center gap-3 py-3 px-4 text-gray-700 hover:text-[#058BF0] hover:bg-blue-50 text-base font-semibold cursor-pointer rounded-lg transition-all duration-200 group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#058BF0] focus-visible:ring-offset-2 focus-visible:bg-blue-50'
						href={data.href || '#'}
						onClick={closeMenu}>
						<span className='group-hover:translate-x-1 transition-transform duration-200'>{data.label}</span>
					</Link>
				)}
			</motion.div>
		);
	}

	// Handle nested dropdown for service categories
	const handleCategoryClick = (sectionId, e) => {
		e.stopPropagation();
		toggleSection(sectionId);
	};

	// Link with dropdown or images
	const isExpanded = expandedSections[data.id] || false;
	
	// Check if nav item or any dropdown item is active
	const isNavItemActive = isActiveRoute(data.href);
	const hasActiveDropdownItem = data.isDropdown && data.type === 'link' && 
		data.dropdown?.some(section => 
			section.sub_section_items?.some(item => isActiveRoute(item.href))
		);
	const hasActiveProduct = data.isDropdown && data.type === 'images' &&
		data.dropdown?.links?.some(link => isActiveRoute(link.href));
	const hasActiveSimpleItem = data.isDropdown && data.type === 'simple' &&
		data.dropdown?.some(item => isActiveRoute(item.href));
	const isActive = isNavItemActive || hasActiveDropdownItem || hasActiveProduct || hasActiveSimpleItem;
	
	return (
		<motion.div
			variants={{
				hidden: {
					x: 20,
					opacity: 0,
				},
				visible: {
					x: 0,
					opacity: 1,
					transition: {
						duration: 0.3,
					},
				},
			}}>
			<div className=' rounded-lg overflow-hidden bg-white '>
					<button
						type="button"
						className={`flex items-center justify-between py-3 px-4 text-base font-semibold cursor-pointer transition-all duration-200 group w-full text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#058BF0] focus-visible:ring-offset-2 rounded-lg ${
							isActive 
								? 'text-[#058BF0] bg-blue-50 font-bold' 
								: 'text-gray-700 hover:text-[#058BF0] hover:bg-blue-50'
						}`}
						onClick={() => toggleSection(data.id)}>
					<div className='flex items-center gap-3'>
						{data.href ? (
							isActive ? (
								<span className='transition-transform duration-200'>{data.label}</span>
							) : (
								<Link
									href={data.href}
									className='group-hover:translate-x-1 transition-transform duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#058BF0] focus-visible:ring-offset-2 rounded-md px-1'
									onClick={(e) => {
										e.stopPropagation();
										closeMenu();
									}}>
									{data.label}
								</Link>
							)
						) : (
							<span className='group-hover:translate-x-1 transition-transform duration-200'>{data.label}</span>
						)}
					</div>
					<motion.div
						animate={{ rotate: isExpanded ? 90 : 0 }}
						transition={{ duration: 0.2 }}
						className='text-gray-400 group-hover:text-[#058BF0]'>
						<ChevronRightIcon className='w-5 h-5' />
					</motion.div>
				</button>
				
				<AnimatePresence>
					{isExpanded && (
						<motion.div
							initial={{ height: 0, opacity: 0 }}
							animate={{ height: 'auto', opacity: 1 }}
							exit={{ height: 0, opacity: 0 }}
							transition={{ duration: 0.3 }}
							className='overflow-hidden'>
							<div className='px-4 pb-4 bg-gray-50'>
								{data.type === 'link' && Array.isArray(data.dropdown) && (
									<div className='space-y-2'>
										{data.dropdown.map((section, sectionIndex) => {
											const isSectionExpanded = expandedSections[section.id] || false;
											return (
												<motion.div
													key={section.id}
													initial={{ opacity: 0, y: 10 }}
													animate={{ opacity: 1, y: 0 }}
													transition={{ delay: 0.1 * sectionIndex }}
													className='bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden'>
													{/* Category Header - Clickable */}
													<button
														type="button"
														className='flex items-center justify-between py-3 px-4 cursor-pointer hover:bg-gray-50 transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#058BF0] focus-visible:ring-offset-2 focus-visible:bg-gray-50 w-full text-left'
														onClick={(e) => handleCategoryClick(section.id, e)}>
														<h3 className='text-sm font-bold text-gray-800 flex items-center gap-2'>
															<span className='w-1 h-1 bg-blue-500 rounded-full'></span>
															{section.sub_section_label}
														</h3>
														<motion.div
															animate={{ rotate: isSectionExpanded ? 90 : 0 }}
															transition={{ duration: 0.2 }}
															className='text-gray-400'>
															<ChevronRightIcon className='w-4 h-4' />
														</motion.div>
													</button>
													
													{/* Category Items - Expandable */}
													<AnimatePresence>
														{isSectionExpanded && (
															<motion.div
																initial={{ height: 0, opacity: 0 }}
																animate={{ height: 'auto', opacity: 1 }}
																exit={{ height: 0, opacity: 0 }}
																transition={{ duration: 0.3 }}
																className='overflow-hidden border-t border-gray-100'>
																<div className='px-4 py-2 bg-gray-50'>
																	<ul className='space-y-1'>
																		{section.sub_section_items.map((item, index) => {
																			const isItemActive = isActiveRoute(item.href);
																			return (
																				<motion.li
																					key={item.id}
																					initial={{ opacity: 0, x: -10 }}
																					animate={{ opacity: 1, x: 0 }}
																					transition={{ delay: 0.1 * (index + 1) }}
																					className='group'>
																						{isItemActive ? (
																							<span className='flex items-center gap-3 py-2.5 px-3 text-sm text-[#058BF0] bg-blue-50 rounded-md transition-all duration-200 cursor-default font-bold'>
																								{item.icon && (
																									<span className='text-[#058BF0] transition-colors duration-200 text-base'>{item.icon}</span>
																								)}
																								<span>{item.label}</span>
																							</span>
																						) : (
																							<Link
																								href={item.href}
																								onClick={closeMenu}
																								className='flex items-center gap-3 py-2.5 px-3 text-sm text-gray-600 hover:text-[#058BF0] hover:bg-blue-50 rounded-md transition-all duration-200 group-hover:translate-x-1 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#058BF0] focus-visible:ring-offset-2 focus-visible:bg-blue-50'>
																								{item.icon && (
																									<span className='text-blue-500 group-hover:text-[#058BF0] transition-colors duration-200 text-base'>{item.icon}</span>
																								)}
																								<span className='font-medium'>{item.label}</span>
																							</Link>
																						)}
																					</motion.li>
																				)
																			})}
																	</ul>
																</div>
															</motion.div>
														)}
													</AnimatePresence>
												</motion.div>
											);
										})}
									</div>
								)}

								{data.type === 'simple' && Array.isArray(data.dropdown) && (
									<ul className='space-y-1 pt-2'>
										{data.dropdown.map((item, index) => {
											const isItemActive = isActiveRoute(item.href);
											return (
												<motion.li
													key={item.id}
													initial={{ opacity: 0, x: -10 }}
													animate={{ opacity: 1, x: 0 }}
													transition={{ delay: 0.1 * (index + 1) }}
													className='group'>
														{isItemActive ? (
															<span className='flex items-center gap-3 py-2.5 px-3 text-sm text-[#058BF0] bg-blue-50 rounded-md transition-all duration-200 cursor-default font-bold'>
																<span>{item.label}</span>
															</span>
														) : (
															<Link
																href={item.href}
																onClick={closeMenu}
																className='flex items-center gap-3 py-2.5 px-3 text-sm text-gray-600 hover:text-[#058BF0] hover:bg-blue-50 rounded-md transition-all duration-200 group-hover:translate-x-1 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#058BF0] focus-visible:ring-offset-2 focus-visible:bg-blue-50'>
																<span className='font-medium'>{item.label}</span>
															</Link>
														)}
													</motion.li>
												)
											})}
									</ul>
								)}

								{data.type === 'images' &&
									!Array.isArray(data.dropdown) &&
									data.dropdown && (
										<div className='space-y-3'>
											
											<ul className='space-y-2'>
												{data.dropdown.links.map((link, index) => {
													const isLinkActive = isActiveRoute(link.href);
													return (
														<motion.li
															key={link.id}
															initial={{ opacity: 0, x: -10 }}
															animate={{ opacity: 1, x: 0 }}
															transition={{ delay: 0.1 * (index + 1) }}
															className='group'>
																{isLinkActive ? (
																	<span className='flex items-center gap-3 py-2 px-3 text-sm text-[#058BF0] bg-blue-50 rounded-md transition-all duration-200 cursor-default font-bold'>
																		<span className='text-[#058BF0] transition-colors duration-200'>-</span>
																		<span>{link.label}</span>
																	</span>
																) : (
																	<Link
																		href={link.href}
																		target={link.target}
																		onClick={closeMenu}
																		className='flex items-center gap-3 py-2 px-3 text-sm text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-md transition-all duration-200 group-hover:translate-x-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#058BF0] focus-visible:ring-offset-2 focus-visible:bg-green-50'>
																		<span className='text-[#058BF0] group-hover:text-green-600 transition-colors duration-200'>-</span>
																		<span className='font-medium'>{link.label}</span>
																	</Link>
																)}
															</motion.li>
														)
													})}
											</ul>
										</div>
									)}
							</div>
						</motion.div>
					)}
				</AnimatePresence>
			</div>
		</motion.div>
	);
};

export default MobileNavItem;